export type ElevenLabsAuth = { signedUrl: string } | { conversationToken: string }

/** Request credentials without exposing access tokens or backend response bodies. */
export async function requestElevenLabsAuth(
    apiURL: string,
    accessToken: string,
    agentId: string,
    connectionType: 'websocket' | 'webrtc',
): Promise<ElevenLabsAuth> {
    if (!accessToken) throw new Error('Your sign-in session is unavailable. Please sign in again.')
    if (!apiURL) throw new Error('Voice authentication API is not configured.')
    const path = connectionType === 'webrtc' ? 'conversation-token' : 'signed-url'
    let url: URL
    try {
        url = new URL(`${apiURL.replace(/\/$/, '')}/api/v1/voice/elevenlabs/${path}`)
    } catch {
        throw new Error('Voice authentication API URL is invalid.')
    }
    url.searchParams.set('agent_id', agentId)
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 15_000)
    try {
        let response: Response
        try {
            response = await fetch(url.toString(), {
                headers: { Authorization: `Bearer ${accessToken}` },
                signal: controller.signal,
            })
        } catch {
            throw new Error(controller.signal.aborted
                ? 'Voice authentication request timed out. Please try again.'
                : 'Cannot reach the voice authentication API. Check your connection or API CORS settings.')
        }
        if (!response.ok) {
            const hint = response.status === 401 ? ' Please sign in again.'
                : response.status === 403 ? ' Access to this voice agent was denied.'
                : response.status === 404 ? ' The voice authentication endpoint is unavailable.'
                : ' Please try again or contact support.'
            throw new Error(`Voice authentication failed (${path}, HTTP ${response.status}).${hint}`)
        }
        let data: unknown
        try {
            data = await response.json()
        } catch {
            throw new Error(controller.signal.aborted
                ? 'Voice authentication request timed out. Please try again.'
                : 'Voice authentication API returned an invalid JSON response.')
        }
        const key = connectionType === 'webrtc' ? 'conversation_token' : 'signed_url'
        const credential = data && typeof data === 'object' ? (data as Record<string, unknown>)[key] : null
        if (typeof credential !== 'string' || !credential.trim()) {
            throw new Error(`Voice authentication API response is missing ${key}.`)
        }
        return connectionType === 'webrtc' ? { conversationToken: credential } : { signedUrl: credential }
    } finally {
        clearTimeout(timeout)
    }
}

export function safeVoiceError(message: string): string {
    return message
        .replace(/(?:https?|wss?):\/\/\S+/gi, '[URL omitted]')
        .replace(/\bBearer\s+\S+/gi, 'Bearer [omitted]')
        .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[token omitted]')
        .slice(0, 500)
}
